/**
 * Shared style editor for the selected page component (ADR-0010 S15 / S15c).
 *
 * Built from the shared `inspectorKit` so it reads exactly like the Properties
 * panel: tidy labelled rows (label left, control right) with hairline
 * separators, grouped into Text / Box sections, plus a component-named section
 * for the node's OWN style tokens (button size/width, image fit/aspect…).
 * Controls are VISUAL (segmented button groups, colour swatches, a `Switch`) —
 * never a native `<select>`. Selecting a control's default option clears the
 * key, so an all-default style serialises as `{}` (node round-trips un-styled).
 */
import { AlignCenter, AlignLeft, AlignRight } from "lucide-react";
import type { ReactNode } from "react";

import { Switch } from "@/components/ui/switch";
import {
  COMPONENT_BOOLS,
  COMPONENT_GROUPS,
  type DimValue,
  DIMENSIONS,
  type StyleBool,
  type StyleControl,
  type StyleProps,
  RESPONSIVE_BOOLS,
  UNIVERSAL_BOOLS,
  UNIVERSAL_GROUPS,
} from "@/lib/pageStyle";

import { DimensionField, Field, type KitOption, Section, Segmented, Swatch } from "./inspectorKit";

export interface StylePanelProps {
  style: StyleProps;
  /** Node type — surfaces the component-specific style section (S15). */
  componentType: string;
  onChange: (style: StyleProps) => void;
}

const ALIGN_ICONS: Record<string, ReactNode> = {
  left: <AlignLeft className="h-3.5 w-3.5" />,
  center: <AlignCenter className="h-3.5 w-3.5" />,
  right: <AlignRight className="h-3.5 w-3.5" />,
};

export function StylePanel({ style, componentType, onChange }: StylePanelProps) {
  // Drop default/false values so an all-default style is `{}` (un-styled).
  const set = (field: string, value: unknown) => {
    const next: StyleProps = { ...style };
    if (value === null || value === false) delete next[field];
    else next[field] = value;
    onChange(next);
  };

  const componentControls = COMPONENT_GROUPS[componentType] ?? [];
  const componentBools = COMPONENT_BOOLS[componentType] ?? [];

  return (
    <div className="flex flex-col gap-4">
      {UNIVERSAL_GROUPS.map((group) => (
        <Section key={group.title} title={group.title}>
          {group.controls.map((ctrl) => (
            <ControlRow key={ctrl.field} ctrl={ctrl} style={style} onSet={set} />
          ))}
          {group.title === "Box" && (
            <>
              {/* Per-direction spacing (Elementor-style 4-field + link). */}
              {DIMENSIONS.map((dim) => (
                <Field key={dim.field} label={dim.label} layout="stack">
                  <DimensionField
                    value={style[dim.field] as DimValue | undefined}
                    onChange={(v) => set(dim.field, v ?? null)}
                  />
                </Field>
              ))}
              {UNIVERSAL_BOOLS.map((b) => (
                <BoolRow key={b.field} bool={b} style={style} onSet={set} />
              ))}
            </>
          )}
        </Section>
      ))}

      {/* Per-breakpoint visibility (S15b) — applies on the live portal; the
          builder preview keeps everything visible/selectable. */}
      <Section title="Responsive">
        {RESPONSIVE_BOOLS.map((b) => (
          <BoolRow key={b.field} bool={b} style={style} onSet={set} />
        ))}
      </Section>

      {(componentControls.length > 0 || componentBools.length > 0) && (
        <Section title={`${cap(componentType)} options`}>
          {componentControls.map((ctrl) => (
            <ControlRow key={ctrl.field} ctrl={ctrl} style={style} onSet={set} />
          ))}
          {componentBools.map((b) => (
            <BoolRow key={b.field} bool={b} style={style} onSet={set} />
          ))}
        </Section>
      )}
    </div>
  );
}

/** Map a `StyleControl`'s options to the kit's option shape (icon for align,
 *  swatch class for colour pickers). */
function kitOptions(ctrl: StyleControl): KitOption[] {
  return ctrl.options.map((o) => ({
    value: o.value,
    label: o.label,
    icon: ctrl.kind === "icons" ? ALIGN_ICONS[o.value] : undefined,
    swatch: o.sw,
  }));
}

function ControlRow({
  ctrl,
  style,
  onSet,
}: {
  ctrl: StyleControl;
  style: StyleProps;
  onSet: (field: string, value: unknown) => void;
}) {
  const current = String(style[ctrl.field] ?? ctrl.options[0].value);
  const defaultValue = ctrl.options[0].value;
  const opts = kitOptions(ctrl);
  return (
    <Field label={ctrl.label}>
      {ctrl.kind === "swatch" ? (
        <Swatch value={current} options={opts} defaultValue={defaultValue} onChange={(v) => onSet(ctrl.field, v)} />
      ) : (
        <Segmented value={current} options={opts} defaultValue={defaultValue} onChange={(v) => onSet(ctrl.field, v)} />
      )}
    </Field>
  );
}

function BoolRow({
  bool,
  style,
  onSet,
}: {
  bool: StyleBool;
  style: StyleProps;
  onSet: (field: string, value: unknown) => void;
}) {
  return (
    <Field label={bool.label}>
      <Switch checked={style[bool.field] === true} onCheckedChange={(v) => onSet(bool.field, v)} />
    </Field>
  );
}

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
